import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { InfoTooltip } from '@components/main-components/tooltip/InfoTooltip';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { BondingCurveSaleDetailsInfoFragmentDoc, getFragment } from '@graphql/reactQuery';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { formatWeiToEthForDisplay } from '@utils/helpers';
import {
  SoundSwapCurrentSupplyInfo,
  SoundSwapMintedInfo,
  SoundSwapPrimarySupplyInfo,
  SoundSwapTotalVolumeInfo,
} from './TooltipTextInfo';
import {
  BoldCell,
  RegularCell,
  Row,
  SaleDetailsContent,
  SaleDetailsHeader,
  SaleDetailsItem,
  SaleDetailsRoot,
  SaleDetailsTrigger,
  Table,
  TableWrapper,
} from './sharedStyles';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment BondingCurveSaleDetailsInfo on Release {
    id
    ...ReleaseUpdates
  }
`);

export const SaleDetails = ({
  data,
}: {
  data: FragmentType<BondingCurveSaleDetailsInfoFragmentDoc>;
}) => {
  const releaseInfo = getFragment(BondingCurveSaleDetailsInfoFragmentDoc, data);

  const {
    data: {
      totalMinted,
      totalSupply,
      saleDetails: { primaryCollected, totalVolume },
    },
  } = useSubscribeRelease({
    release: releaseInfo,
  });

  const { tabletLandscape } = MediaQuery.useContainer();
  const openOnClick = !tabletLandscape;
  return (
    <SaleDetailsRoot type="single" collapsible>
      <SaleDetailsItem value="mint-details">
        <SaleDetailsContent>
          {/* Wrapper is needed because adding padding to the Content causes a stagger on the animation */}
          <TableWrapper>
            <Table>
              <tbody>
                <Row>
                  <BoldCell>
                    Minted
                    <InfoTooltip
                      delayCloseMs={1000}
                      openOnClick={openOnClick}
                      variant="outlined"
                      size={16}
                    >
                      <SoundSwapMintedInfo />
                    </InfoTooltip>
                  </BoldCell>
                  <RegularCell />
                  <RegularCell>{totalMinted}</RegularCell>
                </Row>
                <Row>
                  <BoldCell>
                    Primary Supply
                    <InfoTooltip
                      delayCloseMs={1000}
                      openOnClick={openOnClick}
                      variant="outlined"
                      size={16}
                    >
                      <SoundSwapPrimarySupplyInfo />
                    </InfoTooltip>
                  </BoldCell>
                  <RegularCell />
                  <RegularCell>{primaryCollected}</RegularCell>
                </Row>
                <Row>
                  <BoldCell>
                    Current Supply
                    <InfoTooltip
                      delayCloseMs={1000}
                      openOnClick={openOnClick}
                      variant="outlined"
                      size={16}
                    >
                      <SoundSwapCurrentSupplyInfo />
                    </InfoTooltip>
                  </BoldCell>
                  <RegularCell />
                  <RegularCell>{totalSupply}</RegularCell>
                </Row>
                <Row>
                  <BoldCell>
                    Total Volume
                    <InfoTooltip
                      delayCloseMs={1000}
                      openOnClick={openOnClick}
                      variant="outlined"
                      size={16}
                    >
                      <SoundSwapTotalVolumeInfo />
                    </InfoTooltip>
                  </BoldCell>
                  <RegularCell />
                  <RegularCell>
                    {formatWeiToEthForDisplay({
                      weiValue: totalVolume,
                      maxDecimals: 4,
                    })}{' '}
                    ETH
                  </RegularCell>
                </Row>
              </tbody>
            </Table>
          </TableWrapper>
        </SaleDetailsContent>
        <SaleDetailsHeader>
          <SaleDetailsTrigger>
            <OpenTriggerContent>Hide details</OpenTriggerContent>
            <ClosedTriggerContent className="closed">Show details</ClosedTriggerContent>
            <FontAwesomeIcon icon={faChevronDown} />
          </SaleDetailsTrigger>
        </SaleDetailsHeader>
      </SaleDetailsItem>
    </SaleDetailsRoot>
  );
};

export const OpenTriggerContent = styled('p', {
  display: 'none',
});

export const ClosedTriggerContent = styled('p', {
  display: 'block',
});
